import { inject, Injectable } from '@angular/core';
import { isNumber } from 'es-toolkit/compat';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';

import { StoryWithInsightDto } from '@malou-io/package-dto';
import { CsvAsStringArrays, getPlatformKeysWithStories, isNotNil, PlatformFilterPage } from '@malou-io/package-utils';

import { StoriesApiService } from '#modules/posts/services/stories.api.service';
import { LightRestaurant } from '#modules/restaurants/models/restaurant';
import {
    AbstractCsvService,
    DEFAULT_CSV_PAGINATION,
} from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { AggregatedStatisticsStore } from '#modules/statistics/stores/aggregated-store/aggregated-statistics.store';
import { EnumTranslatePipe } from '#shared/pipes/enum-translate.pipe';

interface Data {
    restaurant: LightRestaurant;
    stories: StoryWithInsightDto[] | null;
}

interface Options {
    restaurants: LightRestaurant[];
}

@Injectable()
export class AggregatedStoriesCsvInsightsService extends AbstractCsvService<Data[], Options> {
    private readonly _enumTranslatePipe = inject(EnumTranslatePipe);
    private readonly _storiesService = inject(StoriesApiService);
    private readonly _aggregatedStatisticsStore = inject(AggregatedStatisticsStore);

    protected override _getData$({ restaurants }: Options): Observable<Data[]> {
        const { endDate, startDate } = this._aggregatedStatisticsStore.datesFilter();
        const platformKeys = getPlatformKeysWithStories().filter((platformKey) =>
            this._aggregatedStatisticsStore.getPlatformsFiltersByPage({ page: PlatformFilterPage.SOCIAL_NETWORKS }).includes(platformKey)
        );

        if (!startDate || !endDate || platformKeys.length === 0) {
            return of([]);
        }

        return forkJoin(restaurants.map((restaurant) => this._storiesService.synchronizeStories$(restaurant.id))).pipe(
            switchMap(() =>
                forkJoin(
                    restaurants.map((restaurant) =>
                        this._storiesService
                            .getStoriesWithInsights$(restaurant.id, {
                                platformKeys,
                                startDate: startDate.toISOString(),
                                endDate: endDate.toISOString(),
                                pagination: { ...DEFAULT_CSV_PAGINATION, skip: 0 },
                            })
                            .pipe(
                                map((stories) => ({ restaurant, stories })),
                                catchError(() => of(null))
                            )
                    )
                ).pipe(map((results) => results.filter(isNotNil)))
            )
        );
    }

    protected override _isDataValid(data: Data[]): boolean {
        return data.some((d) => !!d.stories?.length);
    }

    protected override _getCsvHeaderRow(): CsvAsStringArrays[0] {
        return [
            'Date',
            'Hour',
            'Location',
            'Location Internal Name',
            'Location Address',
            'Platform',
            'Impressions',
            'Reach',
            'Exits',
            'Next story',
            'Previous story',
        ];
    }

    protected override _getCsvDataRows(data: Data[]): string[][] {
        return data
            .map(({ restaurant, stories }) => {
                if (!stories?.length) {
                    return null;
                }
                const locationName = restaurant.name;
                const locationInternalName = restaurant.internalName ?? '';
                const locationAddress = restaurant.getFullFormattedAddress().replace(/,/g, '');

                return stories.map((story) => {
                    const createdAt = new Date(story.createdAt);
                    const date = createdAt.toLocaleDateString();
                    const hour = `${createdAt.getHours()}:${createdAt.getMinutes()}`;
                    const platform = this._enumTranslatePipe.transform(story.key, 'platform_key');

                    return [
                        date,
                        hour,
                        locationName,
                        locationInternalName,
                        locationAddress,
                        platform,
                        this._formatMetric(story.impressions),
                        this._formatMetric(story.reach),
                        this._formatMetric(story.tapsExits),
                        this._formatMetric(story.tapsForward),
                        this._formatMetric(story.tapsBack),
                    ];
                });
            })
            .filter(isNotNil)
            .flat();
    }

    private _formatMetric(value: number | null): string {
        if (!isNumber(value)) {
            return 'N/A';
        }
        return value < 0 ? '< 5' : value.toString();
    }
}
